import { useQuery } from '@tanstack/react-query'
import SingleDropdown from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import LockedIcon from 'ext-ui/svg/icon/LockedIcon'
import React, { ReactElement } from 'react'
import { useSelector } from 'react-redux'
import { useTranslate } from 'src/localization/Trns'
import {
  countriesQueries,
  toCountryListModelMap,
} from 'src/queries/countries/countries.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'

import { kybInvalidMessage } from './kybErrorMessages'
import { kybAddressStyle } from './kybModal.style'
import { ServerAddressMessages } from './kybServerErrors'
import { KybAddressErrors, KybAddressValues } from './paymentsKyb.validation'

export interface KybAddressFieldsetProps {
  legend: string
  /** Unique prefix for the input test ids so multiple address blocks don't collide. */
  idPrefix: string
  value: KybAddressValues
  onChange: (next: KybAddressValues) => void
  errors?: KybAddressErrors
  serverMessages?: ServerAddressMessages
}

/**
 * A structured US address block, US-locked in the MVP. Field order + treatment
 * match Richa's `invoice-pay-prototype` `AddressFields`: **Country first**
 * (locked to "United States" with a lock affordance, NOT a greyed-disabled
 * input), then Line 1, Line 2, City + State (paired), ZIP.
 *
 * State is a **dropdown** — Richa confirmed 2026-07-27 it should match the rest
 * of the app rather than the prototype's own free-text input. The options come
 * from the same server country schema `AddressForm` uses
 * (`countriesQueries` → `countrysMap['US'].fields`'s province `options`), so the
 * state list (and its 2-letter `code` values MT wants for `region`) stay
 * consistent app-wide instead of a hand-maintained list here.
 *
 * DS-gap flag (for [[project_ads_feedback_for_richa]]): ext-ui `TextInput` has
 * no "locked, here's why" treatment — the country field is composed locally
 * (readOnly input + `iconRight` lock) rather than a real DS variant.
 */
export function KybAddressFieldset({
  legend,
  idPrefix,
  value,
  onChange,
  errors,
  serverMessages,
}: KybAddressFieldsetProps): ReactElement {
  const translate = useTranslate()
  const fieldError = (leaf: keyof KybAddressErrors): string | undefined =>
    kybInvalidMessage(translate, errors?.[leaf]) ?? serverMessages?.[leaf]
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { data: countrysMap = {} } = useQuery({
    ...countriesQueries.list(locale),
    select: toCountryListModelMap,
  })

  // The US province ("State") field from the shared country schema — the same
  // source AddressForm renders its state dropdown from. Empty until the
  // (app-wide, usually already-warm) countries query resolves.
  const provinceField = (countrysMap['US']?.fields ?? []).find(
    (field) => field.options,
  )
  const stateOptions = (provinceField?.options ?? []).map((option) => ({
    value: option.code,
    label: option.label,
  }))
  const selectedState = stateOptions.find(
    (option) => option.value === value.region,
  )

  const set =
    (field: keyof KybAddressValues) =>
    (event: React.ChangeEvent<HTMLInputElement>): void =>
      onChange({ ...value, [field]: event.target.value })

  return (
    <fieldset className="KybAddress" css={kybAddressStyle}>
      <legend className="KybAddress-legend">{legend}</legend>
      <div className="KybAddress-grid">
        {/* Country — first + locked. Not `disabled` (that greys the value);
            readOnly keeps "United States" in normal text with a lock icon,
            matching the prototype's explicit "don't make it look disabled". */}
        <div className="span2">
          <TextInput
            label={translate('Country')}
            value={translate('United States')}
            readOnly
            iconRight={
              <LockedIcon
                width={20}
                height={20}
                className="KybAddress-lockIcon"
              />
            }
            testId={`${idPrefix}-country`}
          />
        </div>
        <div className="span2">
          <TextInput
            label={translate('Address Line 1')}
            requirement="required"
            placeholder={translate('Enter Address Line 1')}
            value={value.line1}
            onChange={set('line1')}
            errorText={fieldError('line1')}
            testId={`${idPrefix}-line1`}
          />
        </div>
        <div className="span2">
          <TextInput
            label={translate('Address Line 2')}
            requirement="optional"
            placeholder={translate('Enter Address Line 2')}
            value={value.line2}
            onChange={set('line2')}
            errorText={fieldError('line2')}
            testId={`${idPrefix}-line2`}
          />
        </div>
        <TextInput
          label={translate('City')}
          requirement="required"
          placeholder={translate('Enter City')}
          value={value.locality}
          onChange={set('locality')}
          errorText={fieldError('locality')}
          testId={`${idPrefix}-city`}
        />
        <SingleDropdown
          data-testid={`${idPrefix}-state`}
          label={translate('State')}
          requirement="required"
          placeholder={translate('Select a state')}
          searchable
          options={stateOptions}
          selectedValue={selectedState}
          onSelect={(option): void =>
            onChange({ ...value, region: option.value })
          }
          errorText={fieldError('region')}
        />
        <div className="span2">
          <TextInput
            label={translate('ZIP Code')}
            requirement="required"
            placeholder="#####"
            value={value.postalCode}
            onChange={set('postalCode')}
            errorText={fieldError('postalCode')}
            testId={`${idPrefix}-zip`}
          />
        </div>
      </div>
    </fieldset>
  )
}
